// Manuals (sql/manuals.sql, REPORT-manuals.md): one place for the rules the page, the Dashboard and the linked-manuals boxes share.
//   status      a manual is Pending (uploaded, waiting for a head of department / the Captain), Approved, Draft (generated, being
//               edited) or Rejected; Archived is shown on top of that. Its file is the CURRENT approved version; a newer file
//               waits as a Pending version next to it.
//   rights      Manuals View reads and acknowledges for itself; Edit uploads / renames / replaces / archives / links; Full
//               (head of department, Captain) approves, reverts, chooses who must acknowledge, records for somebody else.
//   links       equipment, risk assessment, checklist, maintenance task, drill, regulation, another manual / SOP (sql/sop-layer.sql) -
//               shown on the manual and on the linked record.
//   SOPs        an SOP is a manual with doc_kind 'sop' (src/lib/sopStandard.js): the Manuals page lists only manuals, the SOPs page
//               only SOPs; everything else here applies to both.
export const CATEGORIES = ['SMS / ISM', 'Equipment', 'Owner\'s manual', 'Builder / yard', 'Class / survey', 'Regulatory', 'Other']

export const STATUS = {
  approved: { label: 'Approved', tone: 'ok' },
  pending: { label: 'Pending approval', tone: 'warn' },
  draft: { label: 'Draft', tone: 'bad' },
  rejected: { label: 'Rejected', tone: 'bad' },
  archived: { label: 'Archived', tone: 'muted' },
}
export const isArchived = m => !!(m && m.archived_at)

// who may see a manual (sql/manuals-2.sql visible_to); tier = manual_viewer_tier(): 1 crew, 2 head of department, 3 Captain
export const VISIBILITY = { all: { label: 'All crew', tier: 1 }, hod: { label: 'Heads of department', tier: 2 }, captain: { label: 'Captain only', tier: 3 } }
export const visibilityOf = m => (m && VISIBILITY[m.visible_to] ? m.visible_to : 'all')
export const visibilityLabel = m => VISIBILITY[visibilityOf(m)].label
// the choices a login may give: up to its own tier (the database refuses a manual it could not see itself)
export const visibilityChoices = tier => Object.entries(VISIBILITY).filter(([, v]) => v.tier <= Math.max(1, Number(tier) || 1)).map(([k, v]) => ({ value: k, label: v.label }))
// a new manual: an Owner's manual starts at heads of department (the database does the same), everything else all crew
export const OWNERS_CATEGORY = "Owner's manual"
export const defaultVisibility = category => (category === OWNERS_CATEGORY ? 'hod' : 'all')
// a crew login (tier 1) cannot upload an Owner's manual: it would not see it afterwards
export const uploadBlocked = (category, tier) => category === OWNERS_CATEGORY && (Number(tier) || 1) < 2
// the status a list shows: archived wins, a manual that has an approved file but a newer pending one is still Approved
export const statusKey = m => (isArchived(m) ? 'archived' : (m && STATUS[m.status] ? m.status : 'draft'))
export const statusInfo = m => STATUS[statusKey(m)]

// my rights from the Manuals level (0 none, 1 view, 2 edit, 3 full - my_perm)
export function rights(level) {
  const l = Number.isFinite(Number(level)) ? Number(level) : 0
  return { view: l >= 1, edit: l >= 2, full: l >= 3, upload: l >= 2, replace: l >= 2, archive: l >= 2, link: l >= 2, approve: l >= 3, revert: l >= 3, chooseAck: l >= 3, ackForOthers: l >= 3, seeWhoRead: l >= 2 }
}

export const sortVersions = vs => (vs || []).slice().sort((a, b) => b.version - a.version)
export const currentVersion = vs => (vs || []).find(v => v.status === 'current') || null
export const pendingVersion = vs => sortVersions(vs).find(v => v.status === 'pending') || null
// a manual uploaded by a browser still on the old page: the file is on the row, there is no version yet - it is Pending too
export function waitingFile(m, vs) {
  const p = pendingVersion(vs)
  // an upload from before versions existed, set back to Pending by sql/manuals-2.sql (nobody had approved it): approve it once
  const old = p && /before (versions|approvals) existed/i.test(p.uploaded_by || '')
  if (p && old) return { version: p.version, file_url: p.file_url, file_name: p.file_name, note: 'uploaded before approvals existed - nobody has approved it yet', by: null, at: p.uploaded_at, legacy: true }
  if (p) return { version: p.version, file_url: p.file_url, file_name: p.file_name, note: p.change_note, by: p.uploaded_by, at: p.uploaded_at, legacy: false }
  if (m && m.status === 'pending' && m.file_url && !(vs || []).length) return { version: 1, file_url: m.file_url, file_name: m.file_name, note: null, by: null, at: m.created_at, legacy: true }
  return null
}
// the file a reader opens: the current approved one (a Pending first upload only for those who may approve it)
export function openFile(m, vs, canApprove) {
  if (m && m.file_url && m.status !== 'pending') return { url: m.file_url, name: m.file_name, version: m.version || 1, pending: false }
  const w = canApprove ? waitingFile(m, vs) : null
  return w ? { url: w.file_url, name: w.file_name, version: w.version, pending: true } : null
}

const fmt = d => { const x = new Date(d); return isNaN(x) ? String(d || '').slice(0, 10) : x.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) }
export const niceDate = fmt
// "v2 - 12 Oct 2026 - Edgar Todd - New outboard"
export function versionLine(v) {
  const bits = ['v' + v.version, fmt(v.decided_at && v.status !== 'pending' ? v.decided_at : v.uploaded_at), v.uploaded_by, v.change_note].filter(Boolean)
  return bits.join(' · ')
}
export const versionStatusLabel = v => ({ current: 'Current', pending: 'Waiting for approval', superseded: 'Replaced', rejected: 'Rejected' }[v.status] || v.status)
// "Approved by Harriet Dunn on 12 Oct 2026" / "Approved - no approver recorded (uploaded before approvals existed)"
export function approvalLine(m) {
  if (!m || m.status !== 'approved') return ''
  return m.approved_by ? 'Approved by ' + m.approved_by + (m.approved_at ? ' on ' + fmt(m.approved_at) : '') : 'Approved - no approver recorded (added before approvals existed)'
}

// ---- who must acknowledge ----
export function ackRule(m) {
  if (!m || !m.ack_required) return 'Not required'
  const roles = m.ack_roles || [], crew = m.ack_crew || []
  if (!roles.length && !crew.length) return 'Everybody on board'
  return [roles.length && roles.join(', '), crew.length && crew.length + ' named crew'].filter(Boolean).join(' + ')
}
export function ackSummary(rows) {
  const list = rows || []
  const done = list.filter(r => r.acknowledged_at), missing = list.filter(r => !r.acknowledged_at)
  return { total: list.length, done: done.length, missing, text: list.length ? done.length + ' of ' + list.length + ' have read it' : 'Nobody is asked yet' }
}

// ---- links ----
export const LINK_KINDS = {
  equipment: { label: 'Equipment', page: 'equipment', plural: 'Equipment' },
  maintenance_task: { label: 'Maintenance task', page: 'mtask', plural: 'Maintenance tasks' },
  risk_assessment: { label: 'Risk assessment', page: 'risk_assessments', plural: 'Risk assessments' },
  checklist: { label: 'Checklist', page: 'checklists', plural: 'Checklists' },
  drill: { label: 'Drill', page: 'drill_matrix', plural: 'Drills' },
  regulation: { label: 'Regulation', page: 'regulations', plural: 'Regulations' },
  manual: { label: 'Manual / SOP', page: 'manuals', plural: 'Manuals and SOPs' },
}
export const isSop = m => !!(m && m.doc_kind === 'sop')
// "SOP-DK-03 Anchoring" / a manual's title
export const docLabel = m => (m ? (isSop(m) && m.doc_number ? m.doc_number + ' ' : '') + (m.title || '') : '')
export const linkKindOf = k => LINK_KINDS[k] || { label: k, page: null, plural: k }
export const linksOf = (links, kind, id) => (links || []).filter(l => l.kind === kind && String(l.target_id) === String(id))
export const linksByManual = (links, manualId) => (links || []).filter(l => l.manual_id === manualId)
// the link rows to add when the user picks targets (one per kind + id, never twice)
export function newLinks(manualId, picks, existing) {
  const have = new Set((existing || []).filter(l => l.manual_id === manualId).map(l => l.kind + '|' + l.target_id))
  const out = []
  for (const p of picks || []) {
    if (!p || !LINK_KINDS[p.kind] || !String(p.id == null ? '' : p.id).trim()) continue
    const k = p.kind + '|' + p.id
    if (have.has(k)) continue
    have.add(k); out.push({ manual_id: manualId, kind: p.kind, target_id: String(p.id), title: String(p.title || p.id).slice(0, 160), note: p.note ? String(p.note).slice(0, 120) : null })
  }
  return out
}

// ---- Dashboard "Needs you" (buildNeeds in dashboardCore.js) ----
// att = manual_attention(): { mine: [{ manual_id, title, version }], waiting: [{ manual_id, title, version, uploaded_by }], unread: [{ manual_id, title, missing, total }] }
export function manualNeeds(att) {
  const a = att || {}
  const mine = (a.mine || []).map(x => ({ name: x.title, meta: 'v' + x.version + ' - read and acknowledge' }))
  const waiting = (a.waiting || []).map(x => ({ name: x.title, meta: 'v' + x.version + (x.uploaded_by ? ' uploaded by ' + x.uploaded_by : '') + ' - waiting for approval' }))
  const unread = (a.unread || []).map(x => ({ name: x.title, meta: x.missing + ' of ' + x.total + ' have not read it', n: x.missing }))
  // review date (sql/manuals-2.sql, Full on Manuals only): overdue, or due within 30 days; days = review date - today (vessel's date)
  const rv = (a.review || []).filter(x => x && x.review_due && Number.isFinite(Number(x.days)))
  const reviewLine = x => ({ name: x.title, days: -Number(x.days), meta: reviewMeta(Number(x.days), x.review_due) })
  const reviewOverdue = rv.filter(x => Number(x.days) < 0).map(reviewLine).sort((p, q) => q.days - p.days)
  const reviewDue = rv.filter(x => Number(x.days) >= 0 && Number(x.days) <= 30).map(reviewLine).sort((p, q) => q.days - p.days)
  return { mine, waiting, unread, reviewOverdue, reviewDue }
}
// "review overdue by 3 days" / "review due today" / "review due in 12 days (8 Nov 2026)"
export function reviewMeta(days, date) {
  const d = Number(days), pl = n => n + (n === 1 ? ' day' : ' days')
  if (d < 0) return 'review overdue by ' + pl(-d)
  if (d === 0) return 'review due today'
  return 'review due in ' + pl(d) + (date ? ' (' + fmt(date) + ')' : '')
}

// A4 portrait with 8 mm margins, rendered 960 px wide: the height of one PDF page in px (the page-break pass of the manuals PDF)
export const MANUAL_PDF_PAGE_PX = 960 * (297 - 16) / (210 - 16)
